import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import apiConfig from '../config/apiConfig';
import './UserInfo.css';
import { getAlignmentTitle, getAlignmentColor } from '../data/alignmentTitles';

// Add this outside your component to track API calls
const lastAccountFetchTime = {
  timestamp: 0,
  minInterval: 30000 // 30 seconds minimum between requests
};

const UserInfo = () => {
  const { user, characterStats } = useContext(UserContext);
  const { logout } = useAuth();
  const [directFetchedData, setDirectFetchedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Add a throttling mechanism for fetch operations
  const fetchUserInfoWithThrottle = useCallback(async () => {
    const now = Date.now();
    
    // Skip if we fetched recently (within the minInterval)
    if (now - lastAccountFetchTime.timestamp < lastAccountFetchTime.minInterval) {
      console.log('Skipping account fetch - too soon since last fetch');
      return;
    }
    
    // Set the timestamp before the fetch to prevent additional calls during this one
    lastAccountFetchTime.timestamp = now;
    
    try {
      setIsLoading(true);
      console.log('Fetching character data directly from API...');
      
      const response = await fetch(apiConfig.accountInfo, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch character data: ${response.status}`);
      }
      
      const userData = await response.json();
      console.log('Direct API response (userData):', userData);
      
      // Store the player character data
      if (userData && userData.playerCharacter) {
        setDirectFetchedData(userData.playerCharacter);
        console.log('Direct API playerCharacter data:', userData.playerCharacter);
      }
    } catch (error) {
      console.error('Error in direct API fetch:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Replace your existing useEffect that triggers the fetch with a debounced version
  useEffect(() => {
    // Only run this effect on initial mount
    fetchUserInfoWithThrottle();
    
    // Set up an interval for periodic updates instead of reactive updates
    const intervalId = setInterval(() => {
      fetchUserInfoWithThrottle();
    }, 60000); // Check once per minute at most
    
    return () => clearInterval(intervalId);
  }, [fetchUserInfoWithThrottle]); // Empty dependency array means this only runs once on mount
  
  // Get character data from all available sources with priority
  const getCharacterData = () => {
    // First try the direct fetched data (most reliable)
    if (directFetchedData && typeof directFetchedData === 'object') {
      console.log('Using directly fetched data:', directFetchedData);
      if (directFetchedData.gold !== undefined || directFetchedData.experience !== undefined) {
        return directFetchedData;
      }
    }
    
    // Then try character stats from context
    if (characterStats && typeof characterStats === 'object') {
      console.log('Using character stats from context:', characterStats);
      if (characterStats.gold !== undefined || characterStats.experience !== undefined) {
        return characterStats;
      }
    }
    
    // Then try user.playerCharacter
    if (user && user.playerCharacter) {
      console.log('Using user.playerCharacter:', user.playerCharacter);
      return user.playerCharacter;
    }
    
    console.log('No valid character data found, using defaults');
    return { gold: 0, experience: 0, level: 1 };
  };
  
  // Get current character data
  const characterData = getCharacterData();
  const gold = characterData.gold ?? 0;
  const experience = characterData.experience ?? 0;
  const level = characterData.level ?? 1;
  
  // Debug output
  useEffect(() => {
    console.log('Character data sources available:', {
      directFetched: directFetchedData,
      characterStatsContext: characterStats,
      userPlayerCharacter: user?.playerCharacter,
      resolvedData: characterData,
      goldValue: gold,
      expValue: experience,
      levelValue: level
    });
  }, [characterStats, user, characterData, directFetchedData, gold, experience, level]);
  
  // Calculate experience bar percentage based on the formula
  const calculateExpPercentage = () => {
    if (!experience) return 0;
    
    // Get current level
    const currentLevel = level;
    
    // Calculate total XP needed for current level
    const currentLevelXP = calculateTotalXPForLevel(currentLevel);
    
    // Calculate total XP needed for next level
    const nextLevelXP = calculateTotalXPForLevel(currentLevel + 1);
    
    // Calculate progress within current level
    const progressInCurrentLevel = experience - currentLevelXP;
    const xpNeededForCurrentLevel = nextLevelXP - currentLevelXP;
    
    // Calculate percentage (capped at 100%)
    const percentage = Math.min((progressInCurrentLevel / xpNeededForCurrentLevel) * 100, 100);
    return percentage;
  };

  // Helper function to calculate total XP needed to reach a specific level
  const calculateTotalXPForLevel = (level) => {
    return Math.floor(134 * Math.pow(level, 1.6));
  };

  // Get alignment values with proper zero handling
  const alignmentObj = {
    good: typeof characterData?.good === 'number' ? characterData.good : 0,
    neutral: typeof characterData?.neutral === 'number' ? characterData.neutral : 0,
    evil: typeof characterData?.evil === 'number' ? characterData.evil : 0
  };
  
  // Get title and color using the alignment object
  const alignmentTitle = getAlignmentTitle(alignmentObj) || "True Neutral";
  const alignmentColor = getAlignmentColor(alignmentObj);
  
  // Get profile image URL
  const getProfileImageUrl = () => {
    const iconUrl = directFetchedData?.icon || user?.playerCharacter?.icon;
    if (!iconUrl) {
      return '/default-avatar.png';
    }
    return `${apiConfig.resources}${iconUrl}`;
  };

  // Get character name
  const getCharacterName = () => {
    if (directFetchedData?.username) return directFetchedData.username;
    if (user?.playerCharacter?.username) return user.playerCharacter.username;
    if (user?.username) return user.username;
    return 'Adventurer';
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      if (typeof logout === 'function') {
        await logout();
      } else {
        console.error('Logout function is not available');
        localStorage.removeItem('auth-token');
        sessionStorage.removeItem('auth-token');
      }
      window.location.reload();
    } catch (error) {
      console.error('Error during logout:', error);
      window.location.reload();
    }
  };

  return (
    <div className="user-info">
      {isLoading && <div className="loading-overlay">Loading...</div>}
      
      <div className="profile-pic-container">
        <img 
          src={getProfileImageUrl()} 
          alt="Profile" 
          className="profile-pic"
        />
      </div>
      <div className="user-details">
        <div className="user-header">
          <Link to="/profile" className="username">
            {getCharacterName()}
          </Link>
          <span className="level">Level {level}</span>
        </div>
        
        <div className="experience-bar-container">
          <div 
            className="experience-bar-fill" 
            style={{ width: `${calculateExpPercentage()}%` }}
          ></div>
          <span className="experience-text">
            {(() => {
              const currentLevel = level;
              const currentLevelXP = calculateTotalXPForLevel(currentLevel);
              const nextLevelXP = calculateTotalXPForLevel(currentLevel + 1);
              const earnedXP = Math.max(0, experience - currentLevelXP);
              const requiredXP = nextLevelXP - currentLevelXP;
              
              return `${Math.floor(earnedXP).toLocaleString()}/${Math.floor(requiredXP).toLocaleString()} XP`;
            })()}
          </span>
        </div>
        
        <div className="character-stats">
          <div className="stat gold">
            <span className="stat-icon">💰</span>
            <span className="stat-value">{gold.toLocaleString()}</span>
          </div>
          <div className="stat alignment" style={{ color: alignmentColor }}>
            <span className="stat-icon">⚖️</span>
            <span className="stat-value">{alignmentTitle}</span>
          </div>
        </div>
      </div>
      <button onClick={handleLogout} className="logout-button">Logout</button>
    </div>
  );
};

export default UserInfo;
